import React, { useCallback, useMemo, useState } from 'react';
import { css, useTheme } from "@emotion/react";
import Carousel from "nuka-carousel";

import { NOOP } from "core/constants";
import useBreakpoint from "core/hooks/useBreakpoint";
import RoundChevronButton from "core/Components/Buttons/RoundChevronButton";
import Image from "core/Components/Image";
import GalleryThemeProvider from "core/Components/Item/GalleryThemeProvider";
import Thumbnail from "core/Components/Item/Thumbnail";
import ThumbnailContainer from "core/Components/Item/ThumbnailContainer";
import { isBetween, isOnly, isUp } from "theme/breakpoints";
import ChevronContainer from "elements/Components/SliderWithImage/components/ChevronContainer";

import { ReactComponent as PlayIcon } from "images/icons/play-triangle-with-circle.svg";

type Props = {
    className?: string,
    imagesAndVideos: [ImageAndVideo],
    onVideoClick?: (url: string) => void,
};

const Gallery: React.FC<Props> = ({
    className = '',
    imagesAndVideos,
    onVideoClick = NOOP,
}) => {
    const theme = useTheme();
    const [slideIndex, setSlideIndex] = useState(0);
    const { breakpoint } = useBreakpoint();
    const [imageHeight, chevronHeight] = useMemo(() => {
        switch (true) {
            case isUp(breakpoint, '5xl'):
                return [653, 80];
            case isBetween(breakpoint, 'xxl', '4xl'):
                return [565, 80];
            case isOnly(breakpoint, 'xl'):
                return [499, 70];
            case isOnly(breakpoint, 'lg'):
                return [399.29, 70];
            case isOnly(breakpoint, 'md'):
                return [450, 60];
            default:
                return [299.6, 60]
        }
    }, [breakpoint]);

    const onThumbnailClick = useCallback((index: number) => {
        setSlideIndex(index);
    }, [setSlideIndex]);

    const chevron = (type: "left" | "right", slideEvent: any) => (
        imagesAndVideos.length > 1 ? (
            <ChevronContainer
                chevronDirection={type}
                imageHeight={imageHeight}
                isChevronYCentre
            >
                <RoundChevronButton
                    type={type}
                    onClick={slideEvent}
                    roundColour={'transparent'}
                    chevronColour={theme.colours.silverChalice2}
                    size={chevronHeight}
                    useThinnerChevron
                />
            </ChevronContainer>
        ) : null
    );

    const sliderImageContainer = ({ hasVideo }: { hasVideo: boolean }) => css`
        width: 560px;
        height: 560px;
        
        ${hasVideo && css`
            cursor: pointer;
        `}
        
        ${theme.breakpoints.up('5xl')} {
            width: 653px;
            height: 653px;
        }
        
        ${theme.breakpoints.between('xxl', '4xl')} {
            width: 565px;
            height: 565px;
        }

        ${theme.breakpoints.only('xl')} {
            width: 499px;
            height: 499px;
        }

        ${theme.breakpoints.only('lg')} {
            width: 399.29px;
            height: 399.29px;
        }

        ${theme.breakpoints.only('md')} {
            width: 478px;
            height: 450px;
        }

        ${theme.breakpoints.down('sm')} {
            width: 318.24px;
            height: 299.6px;
        }
    `;

    const playIconStyle = css`
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 20%;
        z-index: ${theme.zIndex.zIndexOne};
        fill: ${theme.colours.raspberry};
        border-radius: 50%;
        will-change: transform;
    `;

    return (
        <GalleryThemeProvider>
            <Carousel
                slidesToShow={1}
                dragging={false}
                autoplayInterval={6000}
                speed={1500}
                slideIndex={slideIndex}
                renderCenterLeftControls={({ previousSlide }) => (
                    chevron("left", previousSlide)
                )}
                renderCenterRightControls={({ nextSlide }) => (
                    chevron("right", nextSlide)
                )}
                defaultControlsConfig={{
                    pagingDotsStyle: { display: 'none' }
                }}
                wrapAround
            >
                {imagesAndVideos.map((imageAndVideo: ImageAndVideo, index) => (
                    <div
                        key={`gallery-carousel-${index}`}
                        css={sliderImageContainer({ hasVideo: !!imageAndVideo.videoPath })}
                        {...onVideoClick !== NOOP && imageAndVideo.videoPath && {
                            onClick: () => onVideoClick(imageAndVideo.videoPath || '')
                        }}
                    >
                        {imageAndVideo.videoPath && (
                            <PlayIcon css={playIconStyle} />
                        )}
                        <Image
                            image={imageAndVideo.image}
                            useFocusPoint={true}
                        />
                    </div>
                ))}
            </Carousel>
            {imagesAndVideos.length > 1 && (
                <ThumbnailContainer>
                    {imagesAndVideos.map((imageAndVideo: ImageAndVideo, index) => (
                        <Thumbnail
                            key={`gallery-thumbnail-${index}`}
                            className={className}
                            image={imageAndVideo.image}
                            onClick={() => onThumbnailClick(index)}
                            isVideo={!!imageAndVideo.videoPath}
                        />
                    ))}
                </ThumbnailContainer>
            )}
        </GalleryThemeProvider>
    );
};

export default Gallery;